// useViewMode — выбор вида списка (карточки / список) с запоминанием в
// localStorage, per-раздел (ключ). Дефолт — "grid" (карточки): на старте у
// пользователя мало материалов и плитки нагляднее; при росте объёма он сам
// переключается на "list". Выбор переживает reload / cold-start (в т.ч. mobile
// APK — каждая переустановка иначе сбрасывала бы вид на дефолт).
//
// Используется единым переключателем <ViewToggle> во всех разделах-списках.

import { useCallback, useState } from "react";

export type ViewMode = "grid" | "list";

const STORAGE_PREFIX = "studyhub:view:";

function readStored(key: string, fallback: ViewMode): ViewMode {
	try {
		const stored = localStorage.getItem(`${STORAGE_PREFIX}${key}`);
		return stored === "grid" || stored === "list" ? stored : fallback;
	} catch {
		// localStorage недоступен (private mode / SSR-prerender) — in-memory дефолт.
		return fallback;
	}
}

/**
 * @param key Уникальный ключ раздела (`notes`, `flashcards`, `recordings`, …).
 * @param defaultView Дефолт при первом визите. По продуктовому решению — `grid`.
 */
export function useViewMode(
	key: string,
	defaultView: ViewMode = "grid",
): readonly [ViewMode, (next: ViewMode) => void] {
	const [view, setView] = useState<ViewMode>(() => readStored(key, defaultView));

	const update = useCallback(
		(next: ViewMode) => {
			setView(next);
			try {
				localStorage.setItem(`${STORAGE_PREFIX}${key}`, next);
			} catch {
				// Запись недоступна — остаёмся на in-memory значении до конца сессии.
			}
		},
		[key],
	);

	return [view, update] as const;
}
